import { type QueryKey } from '@tanstack/react-query';
import { memo, type ReactElement } from 'react';
import { useProjectsTableController } from '@/features/projects/components/useProjectsTableController';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import type { ProjectSummaryRow } from '@/features/projects/components/ProjectsTable.types';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { ProjectDto, ProjectSummaryDto, QueryResult } from '@flowqa/shared';

type ProjectsTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  noRowsText?: string;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<ProjectDto>>;
  queryKey?: QueryKey;
  summariesQueryFn?: (projectIds: string[]) => Promise<ProjectSummaryDto[]>;
  summariesQueryKey?: QueryKey;
  onRowsChange?: (rows: ProjectDto[]) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  isAuthenticated?: boolean;
};

const ProjectsTableComponent = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  noRowsText = getEmptyStateMessage('entity.noneFound', { entity: 'project' }),
  queryFn,
  queryKey,
  summariesQueryFn,
  summariesQueryKey,
  onRowsChange,
  onDataUpdatedAt,
  showLastUpdated,
  isAuthenticated = true,
}: ProjectsTableProps): ReactElement => {
  const {
    columns,
    gridConfig,
    handleRowsChange,
    queryFn: resolvedQueryFn,
    queryKey: resolvedQueryKey,
    selectedOrganisationId,
    selectedProjectId,
  } = useProjectsTableController({
    persistKey,
    syncStateToUrl,
    queryFn,
    queryKey,
    summariesQueryFn,
    summariesQueryKey,
    onRowsChange,
  });

  return (
    <ServerDataTable<ProjectSummaryRow>
      queryPath="projects/query"
      {...gridConfig}
      queryFn={resolvedQueryFn}
      queryKey={resolvedQueryKey}
      columns={columns}
      getRowId={(row) => row.id}
      isAuthenticated={isAuthenticated}
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      pageSizeOptions={pageSizeOptions}
      initialPageSize={initialPageSize}
      disablePageSizeSelect={disablePageSizeSelect}
      noRowsText={noRowsText}
      interactionMode="static"
      skeletonGetRowHeight={null}
      showLastUpdated={showLastUpdated}
      autoHeight
      onRowsChange={handleRowsChange}
      onDataUpdatedAt={onDataUpdatedAt}
    />
  );
};

export const ProjectsTable = memo(ProjectsTableComponent);
